import { useState } from "react";
import { skipToken, useQuery } from "@tanstack/react-query";

import { searchUsersForLimitedPartner } from "@vega-api/generated/coinvest-deal-permission/coinvest-deal-permission";
import { useDebounce } from "@vega-core/utils/useDebounce";

import {
  DEFAULT_SEARCH_STALE_TIME,
  MIN_SEARCH_QUERY_LENGTH,
  SEARCH_DEBOUNCE_DELAY_MS,
} from "../../../constants/searchConstants";

type UseUserSearchParams = {
  dealId: string;
  limitedPartnerId: string;
  selectedUserIds: ReadonlySet<string>;
};

export const useUserSearch = ({
  dealId,
  limitedPartnerId,
  selectedUserIds,
}: UseUserSearchParams) => {
  const [searchValue, setSearchValue] = useState("");
  const trimmedSearchValue = searchValue.trim();
  const hasSearchableQuery =
    trimmedSearchValue.length >= MIN_SEARCH_QUERY_LENGTH;
  const debouncedSearchValue = useDebounce(
    hasSearchableQuery ? trimmedSearchValue : "",
    SEARCH_DEBOUNCE_DELAY_MS,
  );
  const isDebouncePending =
    hasSearchableQuery && trimmedSearchValue !== debouncedSearchValue;

  const { data, isFetching } = useQuery({
    queryKey: [
      "coinvest-search-users",
      dealId,
      limitedPartnerId,
      debouncedSearchValue,
    ],
    queryFn: debouncedSearchValue
      ? ({ signal }) =>
          searchUsersForLimitedPartner(
            dealId,
            limitedPartnerId,
            { searchQuery: debouncedSearchValue },
            undefined,
            signal,
          )
      : skipToken,
    select: (response) => response.data,
    staleTime: DEFAULT_SEARCH_STALE_TIME,
  });

  const apiUsers = (data ?? []).map((user) => ({
    id: user.id,
    searchValue: `${user.fullName.firstName} ${user.fullName.lastName} ${user.email}`,
    content: null,
    user: {
      id: user.id,
      firstName: user.fullName.firstName,
      lastName: user.fullName.lastName,
      email: user.email,
    },
  }));

  const users = apiUsers.filter((user) => !selectedUserIds.has(user.id));

  return {
    searchValue,
    setSearchValue,
    users,
    hasSearchableQuery,
    isPending: isFetching || isDebouncePending,
  };
};
